/**
 * @file theme.js
 * @description Design token definitions for the ABAcases UI — warm navy palette.
 *   Exports the `T` token map, urgency/type color helpers, and derived lookup utilities.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module theme
 */

// ─── ABA cases Design Tokens — Warm Navy Palette ─────────────────────────

export const T = {
  // ── Surfaces — soft neutral (cream cast removed, faint warmth kept) ────────
  bg:              "#F4F4F2",   // near-neutral, whisper-warm — main app background
  bgDeep:          "#ECEBE7",   // deeper neutral — nested panels / sidebars
  surface:         "#FFFFFF",   // clean white — cards, modals (was cream #FEFCF9)
  surfaceAlt:      "#F4F4F2",   // alt surfaces — matches bg
  surfaceHover:    "#ECEBE7",   // neutral hover
  surfaceElevated: "#FFFFFF",   // modals / popovers
  surfaceSunk:     "#F3F3F1",   // soft sunk tint — zebra rows, track fills, header bands

  // ── Borders — soft neutral ────────────────────────────────────────────────
  border:       "#E6E5E2",      // neutral border
  borderStrong: "#D6D5D1",      // stronger neutral border
  borderSoft:   "#EEEDEA",      // soft hairline
  borderFocus:  "#1E1B4B",      // navy — focus ring

  // ── Text — navy tinted ────────────────────────────────────────────────────
  text:      "#18162E",         // deep navy text
  textMid:   "#3D3A52",         // navy-mid body text
  textSubtle:"#5C5975",         // between textMid and textLight — for secondary labels, helper text, captions
  textLight: "#7C798F",         // muted text
  textXLight:"#ABA8BB",         // x-muted / placeholders

  // ── Primary — deep navy ───────────────────────────────────────────────────
  blue:      "#1E1B4B",         // deep navy (primary interactive)
  blueHover: "#12103A",         // darker navy
  blueLight: "#EEECF8",         // navy tint bg
  blueMid:   "#6B68A8",         // navy mid-tone

  // ── Brand indigo — THE primary call-to-action color on every public page ──
  //    (header, hero, pricing, final CTA bands, sticky mobile CTA). Green below is
  //    reserved for status and money semantics only (paid, reconciled, success).
  indigo:      "#4F46E5",
  indigoHover: "#4338CA",       // pressed / hover fill
  indigoDark:  "#3730A3",       // text-on-light-indigo, deep accents
  indigoSoft:  "#818CF8",       // on-navy accent text (eyebrows, breadcrumbs)
  indigoLight: "#EEF2FF",       // tint bg

  // ── Success — forest green ────────────────────────────────────────────────
  green:      "#0A6B4E",        // forest green
  greenHover: "#085A40",        // darker green
  greenLight: "#D1FAE5",        // green tint bg

  // ── Warning / amber ───────────────────────────────────────────────────────
  yellow:      "#B45309",       // genuine amber — generic warning
  yellowLight: "#FEF3C7",       // amber light bg

  // ── 1099 / urgency — amber (same family) ─────────────────────────────────
  orange:      "#B45309",       // genuine amber
  orangeLight: "#FEF3C7",       // amber light bg

  // ── Danger — red ──────────────────────────────────────────────────────────
  red:      "#dc2626",
  redLight: "#fef2f2",
  // Low fit (2026-10-20, owner: "closer to red"): a deep red orange, distinct from the red kept for real problems.
  lowFit:      "#C2410C",
  lowFitLight: "#FFEDD5",

  // ── Neutral — warm slate ──────────────────────────────────────────────────
  slate:      "#7C798F",
  slateLight: "#F4F4F2",

  // ── Pink (female indicator) ───────────────────────────────────────────────
  pink:      "#db2777",
  pinkLight: "#fdf2f8",

  // ── Shadows — navy tinted ─────────────────────────────────────────────────
  shadow:     "0 1px 3px rgba(18,16,58,0.07), 0 1px 2px rgba(18,16,58,0.05)",
  shadowMd:   "0 4px 16px rgba(18,16,58,0.09), 0 2px 6px rgba(18,16,58,0.06)",
  shadowLg:   "0 12px 40px rgba(18,16,58,0.12), 0 4px 12px rgba(18,16,58,0.07)",
  shadowXl:   "0 20px 60px rgba(18,16,58,0.18), 0 8px 20px rgba(18,16,58,0.10)",
  shadowBlue: "0 4px 20px rgba(18,16,58,0.20), 0 2px 8px rgba(18,16,58,0.12)",
  shadowGreen:"0 4px 20px rgba(10,107,78,0.20), 0 2px 8px rgba(10,107,78,0.12)",
  shadowIndigo:"0 6px 24px rgba(79,70,229,0.4)",
  shadowFocus:"0 0 0 3px rgba(30,27,75,0.14)",  // navy focus ring shadow

  // ── Gradients ─────────────────────────────────────────────────────────────
  gradientBlue:    "linear-gradient(135deg, #1E1B4B 0%, #12103A 100%)",
  gradientBlueSoft:"linear-gradient(135deg, #2D2A6B 0%, #1E1B4B 100%)",
  gradientIndigo:  "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)",
  gradientGreen:   "linear-gradient(135deg, #0A6B4E 0%, #085A40 100%)",
  gradientOrange:  "linear-gradient(135deg, #8B4513 0%, #7A3A10 100%)",
  gradientBg:      "linear-gradient(160deg, #F4F1EC 0%, #EEECF8 50%, #F4F1EC 100%)",
  gradientHero:    "linear-gradient(145deg, #12103A 0%, #1E1B4B 40%, #2D2A6B 100%)",
  gradientSurface: "linear-gradient(180deg, #FEFCF9 0%, #F4F1EC 100%)",

  // ── Border radius ─────────────────────────────────────────────────────────
  radius:   "8px",
  radiusMd: "12px",
  radiusLg: "16px",
  radiusXl: "20px",
  radius2xl:"28px",
  radiusFull:"9999px",

  // ── Map ───────────────────────────────────────────────────────────────────
  mapBg:   "#e8eef4",
  mapGrid: "#dce4ed",
  mapRoad: "#c8d5e3",

  // ── Typography ────────────────────────────────────────────────────────────
  fontBody: "'Inter', 'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
  fontSans: "'Inter', 'DM Sans', sans-serif",

  // ── Status semantic ───────────────────────────────────────────────────────
  statusReview:   "#8B4513",    // amber — under review
  statusActive:   "#0A6B4E",    // forest green — interested / active
  statusDeclined: "#ABA8BB",    // x-muted — closed / declined
};

/**
 * Size scale for app screens (owner call 2026-10-18: no oversized controls). Use these instead of picking sizes:
 *   control       height of buttons, selects and segmented controls in toolbars and panels
 *   controlTouch  the same on phones (touch target)
 *   text          body text, button labels       textSm   secondary lines, meta, chips
 *   label         eyebrows and small captions     title    panel and card titles
 *   pageTitle     the one heading at the top of a screen
 *   radius        corners of controls
 * Style helpers built on this scale live in components/ui/appStyles.js.
 */
export const SIZE = Object.freeze({
  control: 32,
  controlTouch: 40,
  text: 13,
  textSm: 12,
  label: 11,
  title: 15,
  pageTitle: 20,
  radius: 8,
});

export const SETTING_ICONS = {
  Home:      "Home",
  School:    "School",
  Clinic:    "Clinic",
  Community: "Community",
  Remote:    "Remote",
};

export const URGENCY = {
  // isAlert distinguishes urgency states that warrant a filled Badge
  // (true alerts) from descriptive levels that should render as a light
  // Chip alongside other metadata. See Chip-vs-Badge rule in UI.jsx.
  high:   { color: T.orange, bg: T.orangeLight, label: "Urgent",   isAlert: true  },
  // Renamed from "Active" → "Standard" because "Active" collided with
  // the case status (active/paused/closed) and made one chip ambiguous
  // ("Active" on its own could mean either the live-status filter or
  // the medium-urgency filter). "Standard" reads cleanly next to
  // "Urgent" and "Flexible".
  medium: { color: T.blue,   bg: T.blueLight,   label: "Standard", isAlert: false },
  low:    { color: T.green,  bg: T.greenLight,  label: "Flexible", isAlert: false },
};

export const TYPE_COLOR = {
  RBT:   { accent: "#1D4ED8", bg: "#DBEAFE", border: "#BFDBFE" },
  BT:    { accent: "#0D7A6B", bg: "#D1F2E8", border: "#A7E3CC" },
  BCBA:  { accent: "#6D28D9", bg: "#EDE9FE", border: "#DDD6FE" },
  BCABA: { accent: T.orange, bg: T.orangeLight, border: "#FDE8C8" },
};

/**
 * @description Returns the accent/bg color pair for a case type, with a safe fallback.
 * @param {string} type - Case type key ("RBT", "BCBA", "BCABA")
 * @returns {{ accent: string, bg: string }} Color pair
 */
export function typeColor(type) {
  // BT is its own role (2026-10-20): BT openings and BT providers share the BT color.
  return TYPE_COLOR[type] || { accent: T.blue, bg: T.blueLight };
}

/**
 * @description Display label for a case/credential type. The canonical VALUE is
 * "BCABA" (used as the enum key, in comparisons, TYPE_COLOR keys, and the DB),
 * but the correct credential is written "BCaBA" (Board Certified Assistant
 * Behavior Analyst). Use this anywhere a type is rendered to the user; keep the
 * raw value for logic. RBT / BCBA pass through unchanged.
 * @param {string} type - Case type value ("RBT", "BCBA", "BCABA")
 * @returns {string} User-facing label
 */
export function typeLabel(type) {
  if (type === "BCABA") return "BCaBA";
  // BT is its own role (the posting form offers it, providers filter by it), so it shows as BT.
  return type;
}

/**
 * @description The TYPE_COLOR / typeLabel key for a provider's profile role: "rbt" -> "RBT", "bcba" -> "BCBA",
 *   "bcaba" -> "BCABA". BT providers sign up as role "rbt" with the credential "BT", so pass the credential (or the
 *   server credential_kind) as the second argument and that profile reads as "BT". Returns null for a role that is
 *   not a provider role (agency, admin, empty) so callers can keep their own neutral fallback.
 * @param {string} role - Lowercase profile role (rbt, bcba, bcaba); a case type ("RBT", "BCABA") is accepted too
 * @param {string} [credential] - Credential text or credential_kind ("BT", "RBT-123456"); only matters for role rbt
 * @returns {("RBT"|"BT"|"BCBA"|"BCABA"|null)} Key into TYPE_COLOR
 */
export function roleTypeKey(role, credential) {
  const r = String(role || "").toLowerCase();
  if (r === "bcba") return "BCBA";
  if (r === "bcaba") return "BCABA";
  if (r === "bt") return "BT";
  if (r !== "rbt") return null;
  const c = String(credential || "").toUpperCase().replace(/[^A-Z]/g, "");
  return c.startsWith("BT") ? "BT" : "RBT";
}

/**
 * @description Color set (accent, bg, border) for a provider's profile role. Same values as typeColor/TYPE_COLOR,
 *   so a BCBA profile and a BCBA opening always match. Unknown roles get the typeColor fallback.
 * @param {string} role - Lowercase profile role (rbt, bcba, bcaba)
 * @param {string} [credential] - Credential text; "BT" under role rbt shows as BT
 * @returns {{ accent: string, bg: string, border?: string }} Color set
 */
export function roleColor(role, credential) {
  return typeColor(roleTypeKey(role, credential));
}

/**
 * @description Display form of a case code. BT is a role of its own (2026-10-20, owner: a BT opening read as
 * "RBT" next to its BT code), so the stored code shows as is. Kept as the one place codes are formatted.
 * @param {string} code - Stored case code
 * @returns {string} Code to show
 */
export function displayCaseCode(code) {
  return code;
}


/**
 * @description Returns the urgency display metadata for a given urgency key.
 * @param {string} urgency - Urgency key ("high", "medium", "low")
 * @returns {{ color: string, bg: string, label: string }} Display metadata
 */
export function urgencyMeta(urgency) {
  return URGENCY[urgency] || URGENCY.medium;
}
